import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, TextField, Button, Typography, Stack, Tooltip, Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MsiDatagrid from '../../custom/MsiDatagridAuto';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import Icon from '../../icon';

interface KainDialogProps {
    open: boolean;
    onClose: () => void;
    kain: string;
    vendor: string;
    po: string;
    onSave: (qty: number, tag: string) => void;
    onDelete: (id: any) => void;
    detail: any;
}

interface CustomProps extends NumericFormatProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name?: string;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(function NumericFormatCustom(
    props,
    ref
) {
    const { onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name ?? '',
                        value: values.value
                    }
                });
            }}
            thousandSeparator
            valueIsNumericString
        />
    );
});

const KainDialog: React.FC<KainDialogProps> = ({ open, onClose, kain, vendor, po, onSave, detail, onDelete }) => {
    const [qty, setQty] = useState<string>('');
    const [tag, setTag] = useState<string>('');
    const qtyRef = useRef<HTMLInputElement>(null);

    // Auto focus Qty saat dialog terbuka
    useEffect(() => {
        if (open) {
            setTimeout(() => {
                qtyRef.current?.focus();
            }, 100);
        } else {
            // reset ketika dialog ditutup
            setQty('');
            setTag('');
        }
    }, [open]);

    // Handle Enter di Tag
    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    };

    // Handle Save
    const handleSave = () => {
        if (!qty || qty.trim() === '' || !tag || tag.trim() === '') {
            return; // tidak boleh simpan kalau kosong
        }

        onSave(Number(qty), tag); // kirim qty sebagai number
        setQty(''); // reset langsung
        setTag('');
        qtyRef.current?.focus(); // fokus kembali ke Qty setelah save
    };

    const handleDelete = (id: any) => {
        onDelete(id);
    };

    const ActionButtons = ({ row, onDeletes }: {
        row: any;
        onDeletes: (row: any) => void;
    }) => {
        const actions = [
            {
                title: "Hapus Sales Order",
                icon: "mdi:trash",
                color: "red",
                onClick: () => onDeletes(row)
            }
        ];
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'center' }}>
                {actions.map(action => (
                    <Tooltip key={action.title} title={action.title} placement="top">
                        <IconButton onClick={action.onClick}>
                            <Icon icon={action.icon} color={action.color} />
                        </IconButton>
                    </Tooltip>
                ))}
            </Box>
        );
    };

    const actionData = [
        {
            key: "aksi",
            flex: 1,
            sortable: false,
            minWidth: 100,
            field: "aksi",
            align: "center",
            headerAlign: "center",
            headerName: "Aksi",
            renderCell: ({ row }: { row: any }) => (
                <ActionButtons
                    row={row}
                    onDeletes={handleDelete}
                />
            ),
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={4} sx={{ color: '#212121', justifyContent: 'space-between' }}>
                    <Typography variant="h6"><strong>Barang:</strong> {kain}</Typography>
                    <Typography variant="h6"><strong>Vendor:</strong> {vendor}</Typography>
                    <Typography variant="h6"><strong>No. PO:</strong> {po}</Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    {/* Kolom Kiri */}
                    <Grid item xs={8}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Qty"
                                value={qty}
                                onChange={(e) => setQty(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    inputComponent: NumericFormatCustom as any,
                                    inputProps: {
                                        ref: qtyRef
                                    }
                                }}
                            />

                            <TextField
                                label="Tag"
                                value={tag}
                                onChange={(e) => setTag(e.target.value)}
                                onKeyDown={handleTagKeyDown}
                                fullWidth
                            />
                        </Stack>
                    </Grid>

                    {/* Kolom Kanan */}
                    <Grid item xs={4} container justifyContent="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleSave}
                            startIcon={<AddIcon />}
                            sx={{ minWidth: '100px', minHeight: '60px', fontSize: '1.2rem' }}
                        >
                            Simpan
                        </Button>
                    </Grid>
                </Grid>

                {/* DataGrid */}
                <MsiDatagrid array={detail} hiddenColumns={['id']} actions={actionData} />
            </DialogContent>
        </Dialog>
    );
};

export default KainDialog;
